// @flow

import React, { type Node } from 'react';
import styled, { css } from 'styled-components';
import { IfFeature } from '@tectonics/prs/feature';
import { useAllFeatures } from '@tectonics/prs/payment-spec';

import {
  useIncomingPayment,
  getReceipts,
  getCountry,
  getMerchant,
} from '@tectonics/prs/incoming-payment';

import { getMoneyFormat, getMoneyFormatFromCurrency } from '../../money-format';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { getName as getMerchantName } from '@tectonics/prs/merchant';

import {
  getLocalTaxCodeFromString,
  amountToCurrencyString,
  fxToCurrencyString,
} from '@tectonics/prs/money';
import { getAmountCurrencyCode } from '@tectonics/prs/money/amount';

import {
  getInstalmentByNumber,
  getLocalTax,
  getLocalTaxFromCodeSubstring,
  useCurrentReceipt,
} from '@tectonics/prs/receipts';

import { getReceiptAmountExt, getReceiptFx, getSafeInstalmentsPlan } from '@tectonics/prs/receipts/receipt';
import { getInstalmentsNumber, getAmountPartialFirst } from '@tectonics/prs/receipts/receipt/instalments-plan';

import { Tooltip, TooltipIOF, TooltipIVA, TooltipIP, TooltipIGIBP, TooltipPyIVA } from './tooltip';

import { TotalValue } from './total-value';

export function ValuesBox(): Node {
  const allFeatures = useAllFeatures();

  const incomingPayment = useIncomingPayment();
  const receipt = useCurrentReceipt();

  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);

  const fx = getReceiptFx(receipt);
  const iof = getLocalTax(receipt, getLocalTaxCodeFromString('iof'));
  const iva = getLocalTax(receipt, getLocalTaxCodeFromString('ar_iva'));
  const ip = getLocalTax(receipt, getLocalTaxCodeFromString('ar_ip'));
  const igIbp = getLocalTax(receipt, getLocalTaxCodeFromString('ar_ig_ibp'));
  const arIIBB = getLocalTaxFromCodeSubstring(receipt, 'AR_IIBB_');
  const pyIva = getLocalTax(receipt, getLocalTaxCodeFromString('py_iva'));
  const gst = getLocalTax(receipt, getLocalTaxCodeFromString('inr_gst'));

  const [selectedInstalment] = useUserInputValue('selectedInstalment');

  return (
    <DisplayWrapper>
      <DisplayValue>

        <HeaderMessage />

        <IfFeature allFeatures={allFeatures} wanted="display_receipt_original_amount">
          <DisplayReceiptOriginalAmount />
        </IfFeature>

        {fx && (
          <IfFeature allFeatures={allFeatures} wanted="display_receipt_fx_rate">
            <DisplayReceiptFxRate />
          </IfFeature>
        )}

        {iof && (
          <IfFeature allFeatures={allFeatures} wanted="display_receipt_brazilian_tax_iof">
            <Item>
              <ItemLabel>IOF <TooltipIOF/></ItemLabel>
              <ItemValue>
                {formatAmount(iof.amount)}
              </ItemValue>
            </Item>
          </IfFeature>
        )}

        {iva && (
          <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_iva">
            <Item data-testid="display_argentine_iva">
              <ItemLabel>IVA <TooltipIVA/></ItemLabel>
              <ItemValue>
                {formatAmount(iva.amount)}
              </ItemValue>
            </Item>
          </IfFeature>
        )}

        {ip && (parseFloat(ip.rate) != 0) && (
          <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_ip">
            <Item data-testid="display_argentine_ip">
              <ItemLabel>Pais ({parseFloat(ip.rate) * 100}%) <TooltipIP/></ItemLabel>
              <ItemValue>
                {formatAmount(ip.amount)}
              </ItemValue>
            </Item>
          </IfFeature>
        )}

        {igIbp && (parseFloat(igIbp.rate) != 0) && (
          <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_ig_ibp">
            <Item data-testid="display_argentine_ig_ibp">
              <ItemLabel>Ganancias ({parseFloat(igIbp.rate) * 100}%) <TooltipIGIBP IGIBPPercentage={parseFloat(igIbp.rate) * 100}/></ItemLabel>
              <ItemValue>
                {formatAmount(igIbp.amount)}
              </ItemValue>
            </Item>
          </IfFeature>
        )}

        {arIIBB && (parseFloat(arIIBB.rate) != 0) && (
          <IfFeature allFeatures={allFeatures} wanted="display_receipt_argentine_tax_iibb">
            <Item data-testid="display_argentine_iibb">
              <ItemLabel>IIBB ({parseFloat(arIIBB.rate) * 100}%) <TooltipIGIBP IGIBPPercentage={parseFloat(arIIBB.rate) * 100}/></ItemLabel>
              <ItemValue>
                {formatAmount(arIIBB.amount)}
              </ItemValue>
            </Item>
          </IfFeature>
        )}

        {pyIva && (
          <IfFeature allFeatures={allFeatures} wanted="display_receipt_paraguayan_tax_iva">
            <Item data-testid="display_paraguayan_iva">
              <ItemLabel>IVA-C (10%) <TooltipPyIVA/></ItemLabel>
              <ItemValue>
                {formatAmount(pyIva.amount)}
              </ItemValue>
            </Item>
          </IfFeature>
        )}

        {gst && (
          <IfFeature allFeatures={allFeatures} wanted="display_receipt_indian_tax_gst">
            <Item data-testid="display_indian_gst">
              <ItemLabel>GST (18%)</ItemLabel>
              <ItemValue>
                {formatAmount(gst.amount)}
              </ItemValue>
            </Item>
          </IfFeature>
        )}

        {selectedInstalment && Number(selectedInstalment) > 1 && (
          <IfFeature allFeatures={allFeatures} wanted="display_selected_instalment">
            <Separator />

            <Item>
              <ItemLabel><InstalmentsLabelText /></ItemLabel>
              <ItemValue strong>
                <InstalmentPlanText />
              </ItemValue>
            </Item>
          </IfFeature>
        )}

      </DisplayValue>

      <Separator/>

      <DisplayValue>
        <DisplayTotalAmount />
      </DisplayValue>
    </DisplayWrapper>
  );
}

function HeaderMessage(): Node {

  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const merchant = getMerchant(incomingPayment);
  const merchantName = getMerchantName(merchant);

  switch (country) {
    case 'br':
      return (
        <MerchantInfo>
          Sua compra no site {merchantName}
        </MerchantInfo>
      );

    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'py':
    case 'uy':
      return (
        <MerchantInfo>
          Tu compra en el sitio {merchantName}
        </MerchantInfo>
      );
    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
      return (
        <MerchantInfo>
          Your purchase at {merchantName}
        </MerchantInfo>
      );

    default:
      throw new Error(`No message found for merchant-name message in country ${country}`);
  }
}

function DisplayReceiptOriginalAmount(): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = useCurrentReceipt();
  const amount = getReceiptAmountExt(receipt);
  const decimalFormat = getMoneyFormatFromCurrency(getAmountCurrencyCode(amount));
  const formatAmount = amountToCurrencyString(decimalFormat);
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return (
        <Item>
          <ItemLabel>
            Valor da Compra
          </ItemLabel>
          <ItemValue>
            {formatAmount(amount)}
          </ItemValue>
        </Item>
      );
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'py':
    case 'uy':
      return (
        <Item>
          <ItemLabel>
            Valor de la compra
          </ItemLabel>
          <ItemValue>
            {formatAmount(amount)}
          </ItemValue>
        </Item>
      );
    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
      return (
        <Item>
          <ItemLabel>
            Purchase amount
          </ItemLabel>
          <ItemValue>
            {formatAmount(amount)}
          </ItemValue>
        </Item>
      );
    default:
      throw new Error(`No message found for value message in country ${country}`);
  }
}

function DisplayReceiptFxRate(): Node {

  const incomingPayment = useIncomingPayment();
  const receipt = useCurrentReceipt();
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatFx = fxToCurrencyString(decimalFormat);
  const fx = getReceiptFx(receipt);
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return (
        <Item>
          <ItemLabel>
            <p>Taxa de Câmbio</p>
            <Tooltip />
          </ItemLabel>
          <ItemValue>
            {formatFx(fx)}
          </ItemValue>
        </Item>
      );
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'py':
    case 'uy':
      return (
        <Item>
          <ItemLabel>
            <p>Tipo de Cambio</p>
            <Tooltip />
          </ItemLabel>
          <ItemValue>
            {formatFx(fx)}
          </ItemValue>
        </Item>
      );
    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
      return (
        <Item>
          <ItemLabel>
            <p>Exchange Rate</p>
            <Tooltip />
          </ItemLabel>
          <ItemValue>
            {formatFx(fx)}
          </ItemValue>
        </Item>
      );
    default:
      throw new Error(`No message found for value message in country ${country}`);
  }
}

function DisplayTotalAmount(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
      return (
        <Item data-testid="display_total_value">
          <ItemLabel>Total amount</ItemLabel>
          <BigItemValue>
            <TotalValue />
          </BigItemValue>
        </Item>
      );

    default:
      return (
        <Item data-testid="display_total_value">
          <ItemLabel>Valor total</ItemLabel>
          <BigItemValue>
            <TotalValue />
          </BigItemValue>
        </Item>
      );
  }
}

const DisplayWrapper = styled.div`
  background: ${props => props.theme.colors.gray[0]};
  width: 100%;
  padding: 5px 15px 5px 15px;
  @media (min-width: 990px) {
    padding: 0;
  }
  font-family: ${props => props.theme.fontFamily.display};
`;

const MerchantInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 18px;
  font-weight: 400;
  color: ${props => props.theme.colors.gray[3]};
  padding: 15px 0;
`;

const DisplayValue = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.gray[2]};
`;

const Separator = styled.div`
  border: 0.5px solid ${props => props.theme.colors.gray[1]};
  margin: 5px 0;
`;

const ItemLabel = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  font-weight: 400;
  align-items: center;
`;

const ItemValue = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-weight: 700;

  ${props => props.strong && css`
    font-size: 1.2em;
    font-weight: 700;
  `}
`;

const BigItemValue = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  padding: 5px 0;
`;

function InstalmentsLabelText(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  switch (country) {
    case 'br':
      return 'Parcelas';
    case 'mx':
    case 'ar':
    case 'bo':
    case 'co':
    case 'ec':
    case 'pe':
    case 'py':
    case 'uy':
      return 'Cuotas';
    case 'za':
    case 'jm':
      return 'Installments';
    default:
      throw new Error(`InstalmentsLabelText - country not supported ${country}`);
  }
}

function InstalmentPlanText(): Node {
  const incomingPayment = useIncomingPayment();
  const [selectedInstalmentNumber] = useUserInputValue('selectedInstalment');
  const selectedInstalment = getInstalmentByNumber(getReceipts(incomingPayment), Number(selectedInstalmentNumber));

  if (!selectedInstalment) {
    throw new Error('There is no selected instalment to display');
  }

  const instalmentsPlan = getSafeInstalmentsPlan(selectedInstalment);
  const amount = getAmountPartialFirst(instalmentsPlan);
  const formatAmount = amountToCurrencyString(getMoneyFormatFromCurrency(getAmountCurrencyCode(amount)));

  return `${getInstalmentsNumber(instalmentsPlan)}x de ${formatAmount(amount)}`;
}
